import { toNumber } from "../../shared/money";

/*
 * How set up an item is, for Home's setup bars and the Materials filters (so their counts match).
 */

export interface SetupGaps {
    /** No supplier price for it. */
    noSupplier: boolean;
    /** No cost entered (nothing, or 0). */
    noCost: boolean;
    /** Its price is typed in: no pricing rule or group works it out. */
    typed: boolean;
}

export function setupGaps(item: { cost: string }, about: { hasSupplierPrice: boolean; hasPricing: boolean }): SetupGaps {
    return {
        noSupplier: !about.hasSupplierPrice,
        noCost: toNumber(item.cost) <= 0,
        typed: !about.hasPricing,
    };
}

/** A name as people tell items apart by it: ignoring capitals, and spaces at either end or doubled. */
export const nameKey = (n: string) => n.trim().replace(/\s+/g, " ").toLowerCase();

/**
 * Active items synced with ServiceM8 with the same name as another: on a job, a quote or an invoice they can't be told apart.
 * App-only items never get there, so theirs can be anything. Each such item's uuid -> the others (their item numbers tell them apart).
 */
export function sharedNames<T extends { uuid: string; name: string; item_number: string; active: boolean; synced: boolean }>(items: readonly T[]): Map<string, T[]> {
    const byKey = Map.groupBy(
        items.filter((i) => i.active && i.synced && nameKey(i.name)),
        (i) => nameKey(i.name),
    );
    const out = new Map<string, T[]>();
    for (const same of byKey.values()) {
        if (same.length < 2) continue;
        for (const i of same) out.set(i.uuid, same.filter((o) => o.uuid !== i.uuid));
    }
    return out;
}

/** An item number as accounting software matches it: ignoring capitals and spaces at either end. */
export const itemNumberKey = (n: string) => n.trim().toLowerCase();

/**
 * Item numbers that can trip up accounting software (an invoice line it can't match to an item
 * creates a new one): active items with none, and active items sharing one with another active item
 * or bundle. `clashes`: each such item's uuid -> the names it clashes with.
 */
export function itemNumberIssues(items: readonly { uuid: string; name: string; item_number: string; active: boolean }[]) {
    const active = items.filter((i) => i.active);
    const missing = new Set(active.filter((i) => !i.item_number.trim()).map((i) => i.uuid));
    const byKey = new Map<string, { uuid: string; name: string }[]>();
    for (const i of active) {
        const key = itemNumberKey(i.item_number);
        if (key) byKey.set(key, [...(byKey.get(key) ?? []), i]);
    }
    const clashes = new Map<string, string[]>();
    for (const same of byKey.values()) {
        if (same.length < 2) continue;
        for (const i of same) clashes.set(i.uuid, same.filter((o) => o.uuid !== i.uuid).map((o) => o.name));
    }
    return { missing, clashes };
}
